import { useEffect, useMemo, useState } from "react";
import { API_BASE, getAuthHeaders } from "../../api/client";
import { locationIdForStore, locationScopeQuery } from "../../lib/storeScope";
import { toLocalISO } from "../../lib/dates";
import { userEmployeeCode } from "../../lib/userDisplay";

/** Crew: next-week / future preferences + change request on a live day. */
export function ScheduleAvailabilityPanel({ user, lang, store, storeRecords, weekStartISO, weekDates }) {
  const [kind, setKind] = useState("cannot");
  const [prefDate, setPrefDate] = useState("");
  const [timeHint, setTimeHint] = useState("");
  const [note, setNote] = useState("");
  const [mine, setMine] = useState([]);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);
  const locationId = locationIdForStore(store, storeRecords);
  const code = userEmployeeCode(user);

  const dateOptions = useMemo(
    () => (weekDates || []).map((d) => toLocalISO(d)),
    [weekDates]
  );

  function reload() {
    if (!store || !weekStartISO || !code) return;
    const scope = locationScopeQuery(locationId);
    const qs = `${scope}&week_start=${encodeURIComponent(weekStartISO)}&store_id=${encodeURIComponent(store)}&employee_code=${encodeURIComponent(code)}&status=all`;
    fetch(`${API_BASE}/schedule/preferences?${qs}`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : { preferences: [] }))
      .then((body) => setMine(Array.isArray(body?.preferences) ? body.preferences : []))
      .catch(() => setMine([]));
  }

  useEffect(() => {
    if (!prefDate && dateOptions[0]) setPrefDate(dateOptions[0]);
  }, [dateOptions, prefDate]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, weekStartISO, code, locationId]);

  async function submit(e) {
    e.preventDefault();
    if (!store || !prefDate || busy) return;
    setBusy(true);
    setError("");
    setOk("");
    try {
      const res = await fetch(`${API_BASE}/schedule/preferences`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          store_id: store,
          unified_location_id: locationId,
          week_start: weekStartISO,
          preference_date: prefDate,
          kind,
          time_hint: timeHint || undefined,
          note: note || undefined,
          employee_code: code,
          employee_name: user?.name,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = body?.detail;
        if (detail === "next_week_closed") {
          throw new Error(
            lang === "gr"
              ? "Η προθεσμία για την επόμενη εβδομάδα έκλεισε (Τρίτη βράδυ)."
              : "Next-week window closed Tuesday night."
          );
        }
        throw new Error(typeof detail === "string" ? detail : "Could not save");
      }
      setOk(lang === "gr" ? "Αποθηκεύτηκε" : "Saved");
      setNote("");
      reload();
    } catch (err) {
      setError(err?.message || "Failed");
    } finally {
      setBusy(false);
    }
  }

  if (!store) return null;

  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 14,
        padding: 14,
        marginBottom: 16,
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>
        {lang === "gr" ? "Περιορισμοί / προτιμήσεις" : "Restrictions / preferences"}
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.4 }}>
        {lang === "gr"
          ? "Για την επόμενη εβδομάδα μέχρι Τρίτη βράδυ. Το «δεν μπορώ» είναι ισχυρότερο σήμα, όχι μπλοκ για τον manager."
          : "Next week until Tuesday night. “Cannot” is a stronger signal, not a hard block for managers."}
      </div>
      <form onSubmit={submit} style={{ display: "grid", gap: 8 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <select value={kind} onChange={(e) => setKind(e.target.value)} style={fieldStyle}>
            <option value="cannot">{lang === "gr" ? "Δεν μπορώ" : "Cannot work"}</option>
            <option value="prefer_not">{lang === "gr" ? "Προτιμώ όχι" : "Prefer not"}</option>
            <option value="change_request">{lang === "gr" ? "Αίτημα αλλαγής" : "Change request"}</option>
          </select>
          <select value={prefDate} onChange={(e) => setPrefDate(e.target.value)} style={fieldStyle}>
            {dateOptions.map((iso) => (
              <option key={iso} value={iso}>{iso}</option>
            ))}
          </select>
          <input
            value={timeHint}
            onChange={(e) => setTimeHint(e.target.value)}
            placeholder={lang === "gr" ? "π.χ. πρωί" : "e.g. morning"}
            style={{ ...fieldStyle, minWidth: 120 }}
          />
        </div>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={lang === "gr" ? "Σημείωση (προαιρετικό)" : "Note (optional)"}
          style={fieldStyle}
        />
        <button
          type="submit"
          disabled={busy}
          style={{
            border: "none",
            background: "var(--green)",
            color: "#fff",
            borderRadius: 8,
            padding: "8px 12px",
            fontWeight: 800,
            fontSize: 12,
            cursor: busy ? "wait" : "pointer",
            justifySelf: "start",
          }}
        >
          {busy ? "…" : lang === "gr" ? "Υποβολή" : "Submit"}
        </button>
      </form>
      {error && <div style={{ marginTop: 8, color: "#DC2626", fontSize: 12 }}>{error}</div>}
      {ok && <div style={{ marginTop: 8, color: "#16A34A", fontSize: 12, fontWeight: 700 }}>{ok}</div>}
      {mine.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 11, color: "var(--text-secondary)" }}>
          {mine.map((p) => (
            <div key={p.id}>
              {p.preference_date} · {p.kind} · {p.status}
              {p.note ? ` — ${p.note}` : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const fieldStyle = {
  border: "1px solid var(--border)",
  background: "var(--surface2)",
  borderRadius: 8,
  padding: "7px 10px",
  fontSize: 12,
};
